import { Stack, Typography, useTheme } from "@mui/material";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { reminderRowAnchorId } from "web_myplann/NotificationCenter/Utils/reminderScrollParam";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { PinInlineAction } from "web_myplann/Portfolio/Components/Redesign/Detail/PinInlineAction";
import { ReminderFlatEmptyRow } from "web_myplann/Reminders/Components/ReminderFlatEmptyRow";
import { ReminderFlatRow } from "web_myplann/Reminders/Components/ReminderFlatRow";
import { ReminderListRowSkeleton } from "web_myplann/Reminders/Components/ReminderListRowSkeleton";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";

type Props = {
	reminders: Reminder[];
	isLoading?: boolean;
	highlightedId?: string | null;
	canAdd?: boolean;
	onAdd: () => void;
	onTap: (reminder: Reminder) => void;
	onRemove?: (reminder: Reminder) => void;
	lockedIds?: ReadonlySet<string>;
};

export const RemindersFlatList = ({
	reminders,
	isLoading = false,
	highlightedId = null,
	canAdd = true,
	onAdd,
	onTap,
	onRemove,
	lockedIds,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	return (
		<DetailCard label={t(Resources.Reminders.Section.title)}>
			{isLoading ? (
				<Stack
					gap={theme.spacingTokens.lg}
					px={theme.spacingTokens.lg}
					py={theme.spacingTokens.md}
				>
					<ReminderListRowSkeleton />
				</Stack>
			) : (
				<>
					{reminders.length === 0 && <ReminderFlatEmptyRow />}
					{reminders.map((reminder) => {
						const locked = lockedIds?.has(reminder.id) ?? false;

						return (
							<ReminderFlatRow
								key={reminder.id}
								id={reminderRowAnchorId(reminder.id)}
								reminder={reminder}
								highlighted={reminder.id === highlightedId}
								onTap={locked ? undefined : () => onTap(reminder)}
								onRemove={
									locked || !onRemove ? undefined : () => onRemove(reminder)
								}
							/>
						);
					})}
					{canAdd && (
						<PinInlineAction
							label={t(Resources.Reminders.Section.addButton)}
							onClick={onAdd}
						/>
					)}
					{!canAdd && reminders.length === 0 && (
						<Typography
							variant="body2"
							color={theme.palette.grey[500]}
							px={theme.spacingTokens.lg}
							py={theme.spacingTokens.md}
						>
							{t(Resources.Reminders.Form.noTemplatesHint)}
						</Typography>
					)}
				</>
			)}
		</DetailCard>
	);
};
